import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { AuthenticationService, UserProfile } from '@app/authentication/authentication.service';
import { ThemingService } from '@shared/services/theming.service';
import { Theme } from '@shared/themes/default.theme';
import { AptFeatureFlagService } from '@archinsurance-da/arch-portal-tools';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { constants } from './../../@shared/constants';

import { BillingStore } from '@app/interfaces/billing-store.interface';
import { BillingStoreService } from '@app/home/store/billing-store.service';
import { PayerInfoService } from '@app/home/payer-info/payer-info.service';
import { BillingInfoService } from '@shared/services/billing-info.service';

@Component({
  selector: 'app-cyber',
  templateUrl: './cyber.component.html',
  styleUrls: ['./cyber.component.scss'],
  standalone: false,
})
export class CyberComponent implements OnInit {
  priorMcId: string;
  producerId: string;
  theme: Theme;
  priorPolicy: any;
  billingStore: BillingStore;
  successPageTimestamp: string | null = null;
  fromSuccessPage: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private themeService: ThemingService,
    private ffService: AptFeatureFlagService,
    private billingStoreService: BillingStoreService,
    private payerInfoService: PayerInfoService,
    private billingInfoService: BillingInfoService
  ) {
    this.billingStore = this.billingStoreService.getBillingStore();
  }

  get userProfile(): UserProfile {
    return this.authenticationService.userProfile;
  }

  d2cTheme: Theme;
  policyCheckComplete: boolean = false;
  policyStatus: string | null = null;
  policyTimedOut: boolean = false;
  policyTimeoutMessage: string = 'Policy Time out Error could not process the policy';

  cyberForm = this.fb.group({
    q1_radio: ['', Validators.required],
    q2_radio: ['', Validators.required],
    q3_radio: ['', Validators.required],
    // q4_radio: ['', Validators.required],
  });

  ngOnInit() {
    this.themeService.themeChange.subscribe((theme: Theme) => {
      this.theme = theme;

      this.d2cTheme = JSON.parse(JSON.stringify(this.theme));
      this.d2cTheme.agent.phone = this.theme.agent.phoneD2C;
    });

    const routeParams = this.route.snapshot.paramMap;
    const priorMcId = routeParams.get('id');
    this.priorMcId = priorMcId;

    this.checkPolicyTimeout();

    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Accept', 'application/json');

    this.http
      .get<any>(environment.billingPortalConsumerApiHost + constants.policyStatusEndpoint + this.priorMcId, { headers })
      .pipe(
        map((data) => {
          return data;
        })
      )
      .subscribe(
        (response: any) => {
          this.priorPolicy = response;
          this.producerId = response.producerId;
          this.cyberFinished = true;
          this.submitting = false;
          if (response?.statusCode === 'B' || response?.statusCode === 'I') {
            this.showCyberStatusDialog = true;
            this.cyberStatusTitle = 'Unable to Create a Cyber Quote';
            if (response?.statusCode === 'B') this.cyberStatusMessage = 'Policy is already bound.';
            else this.cyberStatusMessage = 'Policy is already issued.';
          }
          console.log('response', response);
        },
        (error) => {
          console.warn('cyber error', error);
        }
      );
  }

  private checkPolicyTimeout(): void {
    if (!this.priorMcId) {
      this.policyCheckComplete = true;
      return;
    }

    // First, check sessionStorage for cyber access eligibility (from success page)
    const cyberAccessData = this.getCyberAccessFromStorage();

    if (cyberAccessData && cyberAccessData.policyId === this.priorMcId) {
      // Valid access from success page - no need to call getPolicy API
      console.log('Cyber - Access from success page, using stored timestamp');
      this.successPageTimestamp = cyberAccessData.timestamp;
      this.fromSuccessPage = true;
      this.policyStatus = 'ISS';
      this.policyTimedOut = this.isTimestampExpired(cyberAccessData.timestamp);
      this.policyCheckComplete = true;

      // Clear the access token after use to prevent reuse
      if (this.policyTimedOut) {
        sessionStorage.removeItem('cyberAccess');
      }
      return;
    }

    // Direct access: use existing AIP_STATUS and processDT logic
    console.log('Cyber - Direct access, checking policy status via API');
    const product = this.billingStore?.product || 'essentialpl';

    this.billingInfoService.getPolicy(this.priorMcId, product, false).subscribe(
      (policyData: any) => {
        const status = policyData?.responseJSON?.policy?.AIP_STATUS;
        const processDt = policyData?.responseJSON?.policy?.processDt;

        this.policyStatus = status ? String(status).toUpperCase() : null;
        this.policyTimedOut = this.isPolicyTimedOut(processDt);
        this.policyCheckComplete = true;
      },
      () => {
        this.policyTimedOut = true;
        this.policyCheckComplete = true;
      }
    );
  }

  private getCyberAccessFromStorage(): { policyId: string; timestamp: string; fromSuccessPage: boolean } | null {
    try {
      const storedData = sessionStorage.getItem('cyberAccess');
      if (!storedData) {
        return null;
      }

      const cyberAccess = JSON.parse(storedData);

      // Validate the stored data structure
      if (!cyberAccess.policyId || !cyberAccess.timestamp || !cyberAccess.fromSuccessPage) {
        sessionStorage.removeItem('cyberAccess');
        return null;
      }

      return cyberAccess;
    } catch (error) {
      console.error('Error parsing cyberAccess from sessionStorage:', error);
      sessionStorage.removeItem('cyberAccess');
      return null;
    }
  }

  get canShowCyber(): boolean {
    return this.policyCheckComplete && this.policyStatus === 'ISS' && !this.policyTimedOut;
  }

  private isPolicyTimedOut(processDt: string): boolean {
    if (!processDt) {
      return true;
    }

    const processDate = this.parseProcessDate(processDt);
    if (!processDate) {
      return true;
    }

    const now = new Date();
    const diffMs = now.getTime() - processDate.getTime();
    const twelveHoursMs = 12 * 60 * 60 * 1000;

    return diffMs > twelveHoursMs;
  }

  private parseProcessDate(processDt: string): Date | null {
    const normalized = processDt.includes('T') ? processDt : processDt.replace(' ', 'T');
    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date;
  }

  private isTimestampExpired(timestamp: string): boolean {
    if (!timestamp) {
      return true;
    }

    const timestampDate = new Date(timestamp);
    if (isNaN(timestampDate.getTime())) {
      return true;
    }

    const now = new Date();
    const diffMs = now.getTime() - timestampDate.getTime();
    const twelveHoursMs = 12 * 60 * 60 * 1000;

    return diffMs > twelveHoursMs;
  }

  private getRedirectUrlForEnv(rawUrl: string): string {
    try {
      const targetUrl = new URL(rawUrl);

      if ((environment.envName === 'local' || environment.envName === 'int') && targetUrl.hostname.includes('stage')) {
        const newBase = new URL(environment.appUrl);
        targetUrl.protocol = environment.envName === 'local' ? 'http' : newBase.protocol;
        targetUrl.hostname = newBase.hostname;
        targetUrl.port = newBase.port;
      }

      return targetUrl.toString();
    } catch {
      return rawUrl;
    }
  }

  submitting: boolean = false;
  cyberFinished: boolean = true;
  cyberTimedOut: boolean = false;
  cyberError: string;
  cyberMessages: string[] = [
    'Your Cyber Application is being processed.',
    'Your request will be ready shortly. Thank you for your patience.',
  ];
  postCyberRequest: boolean = false;
  postCyberTitle: string = '';
  postCyberMessage: string = '';

  showCyberStatusDialog: boolean = false;
  cyberStatusTitle: string = '';
  cyberStatusMessage: string = '';
  //cyberFlag: string = '';
  showCancelDialog: boolean = false;

  cancelCyberForm() {
    this.showCancelDialog = true;
  }

  confirmCancel() {
    this.showCancelDialog = false;
    if (this.producerId === 'P44681') {
      // Progressive flow - go back to BQX
      if (this.billingStore.redirectUrl && this.payerInfoService?.validateUrl(atob(this.billingStore.redirectUrl))) {
        window.location.href = atob(this.billingStore.redirectUrl);
      } else {
        this.router.navigate(['success']);
      }
    } else {
      this.router.navigate(['success']);
    }
  }

  submitCyberForm() {
    this.cyberFinished = false;
    this.submitting = true;
    this.cyberError = '';

    let payload = {
      policyId: this.priorMcId,
      underwriting_evaluation: {
        questions: [
          {
            code: '30102',
            answer: this.cyberForm.value.q1_radio,
          },
          {
            code: '30103',
            answer: this.cyberForm.value.q2_radio,
          },
          {
            code: '30104',
            answer: this.cyberForm.value.q3_radio,
          },
          // {
          //   code: '20102',
          //   answer: this.cyberForm.value.q4_radio,
          // },
        ],
      },
    };
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Authorization', 'Bearer ' + localStorage.getItem('access_token'));

    this.http
      .post<any>(environment.billingPortalApiHost + constants['cyberQuoteEndpoint'], payload, { headers })
      .pipe(
        map((data) => {
          console.log('dataSuccess', data);
          return data;
        })
      )
      .subscribe(
        (response: any) => {
          this.cyberFinished = true;
          this.submitting = false;
          if (
            response?.status === 'QUOTED' &&
            response?.detail?.consumerPaymentUrl &&
            response?.detail?.consumerPaymentUrl.includes('archinsurance.com')
          ) {
            this.postCyberRequest = true;
            this.postCyberTitle = 'Cyber Policy Quoted';
            this.postCyberMessage = 'So far so good, please wait while take you to the next step.';
            //this.cyberFlag = 'Y';
            const paymentUrl = this.getRedirectUrlForEnv(response?.detail?.paymentUrl);
            console.log('paymentUrl', paymentUrl);
            //paymentUrl.searchParams.append('cyberFlag', this.cyberFlag);
            console.log('redirecting to', paymentUrl);
            location.href = paymentUrl;
          } else if (String(response?.detail).toLowerCase() === 'value not found') {
            this.postCyberRequest = true;
            this.postCyberTitle = 'Error Processing Request';
            this.postCyberMessage =
              'We are unable to automatically quote a Cyber policy. The cyber application is invalid.';
          } else {
            this.postCyberRequest = true;
            this.postCyberTitle = 'Unable to Create a Cyber Quote';
            if (this.producerId == 'P44681') {
              this.postCyberMessage =
                'We are unable to automatically quote a cyber for your policy. Please reach out to your agent at businessinsurance@email.progressive.com at 877-232-6408 if you have any questions.';
            } else if (this.producerId == 'P43274') {
              this.postCyberMessage =
                'We are unable to automatically quote a cyber for your policy. Please reach out to your agent at cldirect@nationwide.com at 844-731-7067 if you have any questions.';
            } else {
              this.postCyberMessage =
                'We are unable to automatically quote a cyber for your policy. Please reach out to your agent if you have any questions.';
            }
          }
        },
        (error) => {
          this.postCyberRequest = true;
          this.submitting = false;
          this.postCyberTitle = 'Unable to Create a Cyber Quote';
          if (this.producerId == 'P44681') {
            this.postCyberMessage =
              'We are unable to automatically quote a cyber for your policy. Please reach out to your agent at businessinsurance@email.progressive.com at 877-232-6408 if you have any questions.';
          } else if (this.producerId == 'P43274') {
            this.postCyberMessage =
              'We are unable to automatically quote a cyber for your policy. Please reach out to your agent at cldirect@nationwide.com at 844-731-7067 if you have any questions.';
          } else {
            this.postCyberMessage =
              'We are unable to automatically quote a cyber for your policy. Please reach out to your agent if you have any questions.';
          }
          console.warn('cyber error', error);
        }
      );
  }
}
